/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { useEffect } from 'react';

import values from 'lodash/values';
import { useDispatch, useSelector } from 'react-redux';

import { pushGTMAnalytic } from '../../analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../analytics/GoogleTagManager/GTMOperations';
import {
    tableBasketCheckboxUrlSelector,
    tableBasketCountSelector,
    tableBasketDataSelector,
    tableBasketGtmListSelector,
    tableBasketHasMoreSelector,
    tableBasketListUrlSelector,
} from '../selectors';
import { uncheckItemsFromBasket } from '../slice';
import { tableApi } from '../store';
import { ITableBasketItem } from '../types';

export const useTableBasketApi = () => {

    const dispatch = useDispatch();

    /* API */
    const [getBasketListApi] = tableApi.endpoints.getBasketList.useLazyQuery();
    const [setCheckboxApi] = tableApi.endpoints.check.useMutation();

    /* Selectors */
    const checkboxUrl = useSelector(tableBasketCheckboxUrlSelector);
    const basketListUrl = useSelector(tableBasketListUrlSelector);
    const basketGtmList = useSelector(tableBasketGtmListSelector);

    const basketData = useSelector(tableBasketDataSelector);
    const basketHasMore = useSelector(tableBasketHasMoreSelector);
    const basketCount = useSelector(tableBasketCountSelector);

    /* GA event */
    useEffect(() => {
        if (basketGtmList) {
            dispatch(pushGTMAnalytic({
                operation: GTMOperations.basket.onBasketButtonShow(basketGtmList),
                withUserRole: true,
                withSusrId: true,
                withAccountType: true,
            }));
        }
    }, [basketGtmList]);

    const getBasketList = (pageNumber: number) => {
        if (basketListUrl) {
            getBasketListApi({
                url: basketListUrl,
                from: pageNumber === 0 ? 0 : (basketData?.length ?? 0),
                size: 24,
            });
        }
    };

    const deleteItemsFromBasket = (items: { [itemId: string]: ITableBasketItem }) => {
        if (checkboxUrl) {
            dispatch(uncheckItemsFromBasket(items));
            setCheckboxApi({
                url: checkboxUrl,
                items: values<ITableBasketItem>(items),
                value: false,
            });
        }
    };

    return {
        count: basketCount || 0,
        data: basketData,

        getBasketList,
        deleteItemsFromBasket,

        /* Inifinite loader */
        hasMore: basketHasMore,
        initialLoad: false,
        pageStart: 0,
    };
};
